import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import apiClient from "../api/apiClient";

/**
 * Modal para generar y mostrar el Extracto de Cuentas de un Contrato Inmobiliario.
 *
 * Optimización:
 *  - Reutiliza toda la información ya cargada en el detalle de contrato:
 *      data (cabecera), lines (cuotas), billedByCuota (facturado por cuota),
 *      bpOptions (nombres de titulares) y el cache facturasByCuota.
 *  - Sólo hace fetch de las cuotas que tengan facturas registradas
 *    en REM_CONT_DBCollection y que no estén aún en el cache.
 *  - Los fetchs se lanzan en paralelo (Promise.all) para minimizar tiempo.
 *  - El resultado se escribe de vuelta al cache del padre (setFacturasByCuota),
 *    así no se vuelve a pedir si luego el usuario expande una cuota.
 *
 * UX:
 *  - Formato tipo reporte ejecutivo (tipico extracto bancario/financiero).
 *  - Estilos de impresion @media print -> "Imprimir / PDF" produce una salida
 *    limpia, sin el chrome del modal, la navbar, ni los botones.
 */
const ExtractoCuentasContratoModal = ({
  show,
  onClose,
  data,
  lines,
  billedByCuota,
  moneda,
  docEntry,
  companyDb,
  bpOptions,
  facturasByCuota,
  setFacturasByCuota,
  formatDate,
  formatMoney,
  calcDiasMora,
}) => {
  const [loadingDetalles, setLoadingDetalles] = useState(false);
  const [detallesError, setDetallesError] = useState("");

  const hoy = useMemo(() => {
    const d = new Date();
    const pad = (n) => String(n).padStart(2, "0");
    return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()}`;
  }, []);

  // Titulares (socios distribución) con nombre desde bpOptions.
  const titulares = useMemo(() => {
    const dist = Array.isArray(data?.REM_CONT_DACollection) ? data.REM_CONT_DACollection : [];
    const rows = dist
      .map((r) => {
        const code = String(r?.U_CARDCODE || "").trim();
        if (!code) return null;
        const bp = (bpOptions || []).find(
          (b) => String(b?.Code || "").trim() === code
        );
        const name = String(bp?.Name || "").trim();
        const porc = Number(r?.U_PORCENTAJE || 0);
        const facturar = String(r?.U_FACTURAR || "S").toUpperCase();
        return {
          code,
          name,
          porc,
          facturar: ["N", "NO", "0", "FALSE"].includes(facturar) ? "NO" : "SI",
        };
      })
      .filter(Boolean);

    if (rows.length > 0) return rows;

    // Fallback: titular principal del contrato.
    const code = String(data?.U_CODSR || "").trim();
    const name = String(data?.U_CODSR_NAME || "").trim();
    if (!code) return [];
    return [{ code, name, porc: 100, facturar: "SI" }];
  }, [data, bpOptions]);

  // Mapa DocEntry de facturas por cuota (para llamadas al back).
  const invoiceDocsByCuota = useMemo(() => {
    const dbList = Array.isArray(data?.REM_CONT_DBCollection) ? data.REM_CONT_DBCollection : [];
    const map = {};
    dbList.forEach((r) => {
      const nro = Number(r?.U_NROCUOTA);
      const de = Number(r?.U_NRODOCU);
      if (Number.isNaN(nro) || Number.isNaN(de) || de <= 0) return;
      if (!map[nro]) map[nro] = new Set();
      map[nro].add(de);
    });
    // Set -> Array
    Object.keys(map).forEach((k) => (map[k] = Array.from(map[k])));
    return map;
  }, [data]);

  // Al abrir: pre-carga las facturas asociadas de cuotas que tengan facturas y no estén cacheadas.
  useEffect(() => {
    if (!show) return;
    let alive = true;

    const cuotasAprecargar = Object.keys(invoiceDocsByCuota)
      .map((k) => Number(k))
      .filter((nro) => !Array.isArray(facturasByCuota?.[nro])); // ya cacheadas -> omitir

    if (cuotasAprecargar.length === 0) return;

    const load = async () => {
      setLoadingDetalles(true);
      setDetallesError("");
      try {
        // Lanzamos TODO en paralelo y acumulamos por cuota.
        const tasks = cuotasAprecargar.flatMap((nro) =>
          (invoiceDocsByCuota[nro] || []).map((de) =>
            apiClient
              .get(
                `/inmobiliaria/facturas/${de}/cuotas/${nro}/detalle`,
                companyDb ? { params: { CompanyDB: companyDb } } : undefined
              )
              .then((res) => ({ nro, rows: Array.isArray(res?.data) ? res.data : [] }))
              .catch(() => ({ nro, rows: [] })) // si una falla, no rompe las demás
          )
        );

        const results = await Promise.all(tasks);
        if (!alive) return;

        // Consolidamos por cuota.
        const acc = {};
        results.forEach(({ nro, rows }) => {
          if (!acc[nro]) acc[nro] = [];
          acc[nro].push(...rows);
        });

        // Escribimos al cache del padre (mantiene datos previos intactos).
        setFacturasByCuota((prev) => ({ ...(prev || {}), ...acc }));
      } catch (e) {
        if (!alive) return;
        setDetallesError(
          e?.response?.data?.error || e?.message || "Error cargando facturas asociadas"
        );
      } finally {
        if (alive) setLoadingDetalles(false);
      }
    };

    load();
    return () => {
      alive = false;
    };
    // Solo dispara al abrir el modal o si cambia el contrato.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, docEntry]);

  // ===== Cálculos de cabecera financiera =====
  const cantCuotas = Number(data?.U_CANTCUO || 0);
  const entregaInicial = Number(data?.U_ENTINICIAL || 0);

  // Monto facturado de una cuota. Las cuotas en estado "Y" sin facturas
  // vinculadas en la app (entrega inicial, cuotas facturadas por fuera) se toman
  // como facturadas por su monto completo, para que la columna sume todo lo que
  // figura como Facturado.
  const facturadoDeCuota = (l) => {
    const nro = Number(l?.U_NROCUO);
    const mc = Number(l?.U_MONTOCUO || 0) || 0;
    const fac = Number(billedByCuota?.[nro] || 0) || 0;
    const estado = String(l?.U_ESTADO || "").trim().toUpperCase();
    if (estado === "Y" && fac <= 0) return mc;
    return fac;
  };

  // Totales de cuotas.
  // Se calculan a partir de las LÍNEAS REALES (REM_CONT_DCollection) para que
  // los totales de abajo siempre coincidan con lo que el usuario ve en la tabla,
  // incluso si hubo redondeos, última cuota con ajuste, o cambios manuales sobre
  // U_MONTOCUO que ya no coincidan con (cantCuotas × U_MONTCUO) de la cabecera.
  const totalesCuotas = useMemo(() => {
    let totalMonto = 0;
    let totalFacturado = 0;
    let totalSaldo = 0;
    let deudaVencida = 0; // saldo de cuotas vencidas (días atraso > 1, no facturadas)
    (lines || []).forEach((l) => {
      const mc = Number(l?.U_MONTOCUO || 0) || 0;
      const fac = facturadoDeCuota(l);
      const estado = String(l?.U_ESTADO || "").trim().toUpperCase();
      totalMonto += mc;
      totalFacturado += fac;
      if (estado !== "Y") {
        const saldo = Math.max(mc - fac, 0);
        totalSaldo += saldo;
        const dias = calcDiasMora ? calcDiasMora(l?.U_FECHAV) : 0;
        if (dias > 1) deudaVencida += saldo;
      }
    });
    return {
      totalMonto,
      totalFacturado,
      totalSaldo,
      deudaVencida,
      saldoAVencer: Math.max(totalSaldo - deudaVencida, 0),
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lines, billedByCuota, calcDiasMora]);

  // ¿La entrega inicial ya figura como cuota 0 dentro de la tabla?
  const entregaEsCuotaCero = useMemo(
    () => (lines || []).some((l) => Number(l?.U_NROCUO) === 0),
    [lines]
  );

  // Total de la venta = suma real de las cuotas de la tabla.
  // Cuando la entrega inicial viene cargada como cuota 0 ya está incluida en esa
  // suma, así que sumarla de nuevo la duplicaba (p. ej. 99.310 + 23.367 = 122.677).
  // Solo se suma aparte si el contrato no la tiene como línea.
  const montoTotalVenta = entregaEsCuotaCero
    ? totalesCuotas.totalMonto
    : entregaInicial + totalesCuotas.totalMonto;

  // Monto financiado = total de la venta menos la entrega inicial.
  const montoFinanciado = Math.max(montoTotalVenta - entregaInicial, 0);

  const estadoLabel = (l) => {
    const estado = String(l?.U_ESTADO || "").trim().toUpperCase();
    if (estado === "Y") return "Facturado";
    if (estado === "P") return "Parcial";
    // N: pendiente o vencido
    const dias = calcDiasMora ? calcDiasMora(l?.U_FECHAV) : 0;
    if (dias > 0) return "Vencido";
    return "Pendiente";
  };

  const handlePrint = () => {
    window.print();
  };

  if (!show) return null;

  // Portal a <body>: así el CSS de impresión puede ocultar el resto de la app
  // con display:none y el extracto arranca en la primera página del PDF.
  return createPortal(
    <div className="extracto-print">
      {/* Estilos específicos del extracto. Se aplican en pantalla y al imprimir. */}
      <style>{`
        .extracto-overlay {
          position: fixed;
          inset: 0;
          background: rgba(0,0,0,0.5);
          z-index: 1050;
          overflow-y: auto;
        }
        .extracto-container {
          max-width: 1100px;
          margin: 24px auto;
          background: #fff;
          border-radius: 6px;
          box-shadow: 0 10px 30px rgba(0,0,0,0.2);
          overflow: hidden;
        }
        .extracto-toolbar {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 12px 20px;
          background: #f5f7fa;
          border-bottom: 1px solid #dee2e6;
        }
        .extracto-doc {
          padding: 28px 32px;
          font-size: 13px;
          color: #212529;
          line-height: 1.4;
        }
        .extracto-doc h1 {
          font-size: 20px;
          margin: 0 0 2px;
          letter-spacing: 0.5px;
        }
        .extracto-doc h2 {
          font-size: 14px;
          margin: 14px 0 6px;
          padding-bottom: 4px;
          border-bottom: 2px solid #0d6efd;
          color: #0d6efd;
          text-transform: uppercase;
          letter-spacing: 0.8px;
        }
        .extracto-header-top {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          border-bottom: 3px solid #0d6efd;
          padding-bottom: 10px;
          margin-bottom: 14px;
        }
        .extracto-meta { text-align: right; font-size: 12px; color: #555; }
        .extracto-kv {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 6px 18px;
          font-size: 12.5px;
        }
        .extracto-kv .k {
          color: #6c757d;
          font-size: 11px;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }
        .extracto-kv .v { font-weight: 600; }
        .extracto-doc table {
          width: 100%;
          border-collapse: collapse;
          font-size: 12px;
        }
        .extracto-doc table.main {
          table-layout: fixed;
        }
        .extracto-doc table.main thead th {
          background: #0d6efd;
          color: #fff;
          padding: 6px 8px;
          text-align: left;
          font-weight: 600;
          font-size: 11.5px;
          text-transform: uppercase;
          letter-spacing: 0.3px;
        }
        .extracto-doc table.main thead th.right { text-align: right; }
        .extracto-doc table.main thead th.center { text-align: center; }
        .extracto-doc table.main tbody td {
          padding: 6px 8px;
          border-bottom: 1px solid #e9ecef;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        .extracto-doc table.main tbody tr.cuota-facturada td { background: #f0f9f3; }
        .extracto-doc table.main tbody tr.cuota-vencida td { background: #fff3f3; }
        .extracto-doc table.main tbody tr.factura-row td {
          background: #fafbfc;
          font-size: 11px;
          padding: 3px 8px;
          border-bottom: 1px dashed #e9ecef;
          color: #495057;
        }
        .extracto-doc table.main tbody tr.factura-row .fac-label {
          color: #6c757d;
          font-style: italic;
          margin-right: 4px;
        }
        .extracto-doc table.main tfoot td {
          padding: 8px;
          font-weight: 700;
          border-top: 2px solid #212529;
          background: #f8f9fa;
        }
        .extracto-totals {
          display: flex;
          justify-content: flex-end;
          gap: 12px;
          flex-wrap: wrap;
          margin-top: 14px;
          font-size: 13px;
        }
        .extracto-totals .box {
          border: 1px solid #dee2e6;
          padding: 8px 14px;
          min-width: 170px;
          border-radius: 4px;
          background: #f8f9fa;
        }
        .extracto-totals .box .lbl { font-size: 10.5px; color: #6c757d; text-transform: uppercase; letter-spacing: 0.4px; }
        .extracto-totals .box .val { font-size: 14px; font-weight: 700; }
        .extracto-totals .box.deuda { background: #f8d7da; border-color: #f1aeb5; color: #842029; }
        .extracto-totals .box.saldo { background: #fff3cd; border-color: #ffe69c; }
        .extracto-footer {
          margin-top: 20px;
          font-size: 11px;
          color: #6c757d;
          text-align: center;
          border-top: 1px solid #dee2e6;
          padding-top: 10px;
        }
        .right { text-align: right; }
        .center { text-align: center; }
        .text-muted { color: #6c757d; }
        .mono { font-variant-numeric: tabular-nums; }

        /* ===== Impresion ===== */
        @media print {
          /* El extracto se monta como hijo directo de <body> (portal). Se saca del
             flujo todo lo demás con display:none: con visibility:hidden la página de
             detalle seguía ocupando espacio y el extracto salía en la 3ra página. */
          body > *:not(.extracto-print) { display: none !important; }
          html, body { height: auto !important; overflow: visible !important; background: #fff !important; }
          .extracto-overlay {
            position: static !important;
            overflow: visible !important;
            background: #fff !important;
          }
          .extracto-container {
            box-shadow: none !important;
            margin: 0 !important;
            max-width: 100% !important;
            border-radius: 0 !important;
          }
          .extracto-toolbar { display: none !important; }
          /* El margen lo da @page; sin padding extra para no dejar espacios a los lados. */
          .extracto-doc { padding: 0 !important; }
          /* Evitar cortes feos entre título y su sección, y dentro de filas. */
          .extracto-doc h2 { break-after: avoid; }
          .extracto-doc table.main tbody tr { break-inside: avoid; }
          .extracto-doc table.main thead { display: table-header-group; }
          @page { size: A4 portrait; margin: 9mm; }
        }
      `}</style>

      <div className="extracto-overlay" role="dialog" aria-modal="true">
        <div className="extracto-container">
          <div className="extracto-toolbar">
            <strong>Extracto de Cuentas</strong>
            <div className="d-flex gap-2">
              <button type="button" className="btn btn-sm btn-primary" onClick={handlePrint}>
                Imprimir / Guardar PDF
              </button>
              <button type="button" className="btn btn-sm btn-outline-secondary" onClick={onClose}>
                Cerrar
              </button>
            </div>
          </div>

          <div className="extracto-doc">
            {/* === Cabecera === */}
            <div className="extracto-header-top">
              <div>
                <h1>EXTRACTO DE CUENTAS</h1>
                <div className="text-muted">Contrato inmobiliario N° {docEntry}</div>
              </div>
              <div className="extracto-meta">
                <div><strong>Emitido:</strong> {hoy}</div>
                <div>Moneda: {moneda || "-"}</div>
              </div>
            </div>

            <h2>Datos del contrato</h2>
            <div className="extracto-kv">
              <div><div className="k">Fecha de contrato</div><div className="v">{formatDate(data?.U_FECHAC) || "-"}</div></div>
              <div><div className="k">Vencimiento</div><div className="v">{formatDate(data?.U_FECHAV) || "-"}</div></div>
              <div><div className="k">Estado</div><div className="v">{data?.U_ESTADO || "-"}</div></div>
              <div><div className="k">Moneda</div><div className="v">{moneda || "-"}</div></div>
              <div style={{ gridColumn: "span 4" }}>
                <div className="k">Unidad</div>
                <div className="v">{data?.U_UNIDAD_DESC || data?.U_CODLOTE || "-"}</div>
              </div>
            </div>

            {/* === Titulares === */}
            <h2>Titular{titulares.length > 1 ? "es" : ""}</h2>
            {titulares.length === 0 ? (
              <div className="text-muted">Sin titulares registrados.</div>
            ) : titulares.length === 1 ? (
              <div className="extracto-kv">
                <div style={{ gridColumn: "span 4" }}>
                  <div className="k">Nombre</div>
                  <div className="v">{titulares[0].name || titulares[0].code}</div>
                </div>
              </div>
            ) : (
              <table className="main">
                <thead>
                  <tr>
                    <th>Nombre</th>
                    <th className="right" style={{ width: "12%" }}>% Distribución</th>
                    <th className="center" style={{ width: "12%" }}>Facturar</th>
                  </tr>
                </thead>
                <tbody>
                  {titulares.map((t, i) => (
                    <tr key={i}>
                      <td>{t.name || t.code}</td>
                      <td className="right mono">{t.porc}%</td>
                      <td className="center">{t.facturar}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}

            {/* === Resumen de contrato === */}
            <h2>Resumen de contrato</h2>
            <div className="extracto-kv">
              <div>
                <div className="k">Monto total de la venta</div>
                <div className="v">{formatMoney(montoTotalVenta, moneda)}</div>
              </div>
              {entregaInicial > 0 && (
                <div>
                  <div className="k">Entrega inicial</div>
                  <div className="v">{formatMoney(entregaInicial, moneda)}</div>
                </div>
              )}
              {cantCuotas > 1 && (
                <>
                  <div>
                    <div className="k">Monto financiado</div>
                    <div className="v">{formatMoney(montoFinanciado, moneda)}</div>
                  </div>
                  <div>
                    <div className="k">Cant. de cuotas</div>
                    <div className="v">{cantCuotas}</div>
                  </div>
                </>
              )}
            </div>

            {/* === Detalle de cuotas === */}
            <h2>Detalle de cuotas</h2>

            {loadingDetalles && (
              <div className="text-muted" style={{ marginBottom: 8 }}>
                Cargando facturas asociadas...
              </div>
            )}
            {detallesError && (
              <div style={{ color: "#b02a37", marginBottom: 8 }}>{detallesError}</div>
            )}

            <table className="main">
              <thead>
                <tr>
                  <th style={{ width: "6%" }}>Nro</th>
                  <th style={{ width: "14%" }}>Vencimiento</th>
                  <th className="right" style={{ width: "17%" }}>Monto cuota</th>
                  <th className="right" style={{ width: "17%" }}>Facturado</th>
                  <th className="right" style={{ width: "17%" }}>Saldo</th>
                  <th className="center" style={{ width: "11%" }}>Días atraso</th>
                  <th style={{ width: "18%" }}>Estado</th>
                </tr>
              </thead>
              <tbody>
                {(lines || []).length === 0 && (
                  <tr>
                    <td colSpan={7} className="center text-muted">Sin cuotas</td>
                  </tr>
                )}
                {(lines || []).map((l) => {
                  const nro = Number(l?.U_NROCUO);
                  const mc = Number(l?.U_MONTOCUO || 0);
                  const fac = facturadoDeCuota(l);
                  const estado = String(l?.U_ESTADO || "").trim().toUpperCase();
                  const saldo = estado === "Y" ? 0 : Math.max(mc - fac, 0);
                  // Pedido del negocio: solo mostrar días de atraso si > 1 y la cuota está pendiente.
                  const diasAtraso = estado !== "Y" && calcDiasMora ? calcDiasMora(l?.U_FECHAV) : 0;
                  const mostrarDias = estado !== "Y" && diasAtraso > 1;

                  const facturas = Array.isArray(facturasByCuota?.[nro]) ? facturasByCuota[nro] : [];
                  const rowClass =
                    estado === "Y" ? "cuota-facturada" : mostrarDias ? "cuota-vencida" : "";

                  return (
                    <React.Fragment key={String(l?.LineId ?? nro)}>
                      <tr className={rowClass}>
                        <td>{nro}</td>
                        <td>{formatDate(l?.U_FECHAV)}</td>
                        <td className="right mono">{formatMoney(mc, moneda)}</td>
                        <td className="right mono">{fac > 0 ? formatMoney(fac, moneda) : "-"}</td>
                        <td className="right mono">{formatMoney(saldo, moneda)}</td>
                        <td className="center mono">
                          {mostrarDias ? (
                            <span style={{ color: "#b02a37", fontWeight: 600 }}>{diasAtraso}</span>
                          ) : (
                            "-"
                          )}
                        </td>
                        <td>{estadoLabel(l)}</td>
                      </tr>

                      {facturas.length > 0 && facturas.map((f, i) => (
                        <tr key={`fac-${nro}-${i}`} className="factura-row">
                          <td></td>
                          <td colSpan={2}>
                            <span className="fac-label">↳ Factura N°</span>{" "}
                            <strong>{f?.NroFactura || "-"}</strong>
                            {f?.NroDocumento ? ` (Doc ${f.NroDocumento})` : ""}
                            {"  ·  "}
                            {formatDate(f?.Fecha)}
                          </td>
                          <td className="right mono">{formatMoney(f?.Monto, moneda)}</td>
                          <td
                            colSpan={3}
                            style={{ fontSize: 10.5, wordBreak: "break-all", color: "#6c757d" }}
                            title={f?.CDC || ""}
                          >
                            {f?.CDC ? `CDC: ${f.CDC}` : ""}
                          </td>
                        </tr>
                      ))}
                    </React.Fragment>
                  );
                })}
              </tbody>
              <tfoot>
                <tr>
                  <td colSpan={2}>TOTALES</td>
                  <td className="right mono">{formatMoney(totalesCuotas.totalMonto, moneda)}</td>
                  <td className="right mono">{formatMoney(totalesCuotas.totalFacturado, moneda)}</td>
                  <td className="right mono">{formatMoney(totalesCuotas.totalSaldo, moneda)}</td>
                  <td colSpan={2} />
                </tr>
              </tfoot>
            </table>

            {/* === Totales destacados === */}
            <div className="extracto-totals">
              <div className="box">
                <div className="lbl">Total Contrato</div>
                <div className="val mono">{formatMoney(montoTotalVenta, moneda)}</div>
              </div>
              <div className="box">
                <div className="lbl">Total Facturado</div>
                <div className="val mono">{formatMoney(totalesCuotas.totalFacturado, moneda)}</div>
              </div>
              <div className="box deuda">
                <div className="lbl">Deuda Total Vencida</div>
                <div className="val mono">{formatMoney(totalesCuotas.deudaVencida, moneda)}</div>
              </div>
              <div className="box saldo">
                <div className="lbl">Deuda a Vencer</div>
                <div className="val mono">{formatMoney(totalesCuotas.saldoAVencer, moneda)}</div>
              </div>
            </div>

            <div className="extracto-footer">
              Documento generado automáticamente el {hoy}. Este extracto refleja el estado
              del contrato al momento de su emisión.
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default ExtractoCuentasContratoModal;
